import { FC, useEffect, useState, ReactNode } from 'react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { graphql, useFragment } from 'react-relay';
import {
    useAuctionPricing,
    useAuctionState,
    useAuctionSecondChanceInfo,
} from 'dibs-auctions/exports/hooks';
import { useOriginalListPrice } from 'dibs-auctions/exports/OriginalListPrice';
import { useAuctionBidModalState } from '../hooks/useAuctionBidModalState';
import { trackAuctionBidClick } from 'dibs-auctions-bidding-modal/exports/bidModalTrackingHelpers';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { useSharedAuctionContext } from '../PdpSharedAuctionContext/PdpSharedAuctionContext';

// components
import { PdpSharedAuctionClosed } from '../PdpSharedAuctionClosed/PdpSharedAuctionClosed';
import { PdpSharedAuctionPreview } from '../PdpSharedAuctionPreview/PdpSharedAuctionPreview';
import { PdpSharedAuctionActionBid } from '../PdpSharedAuctionActions/PdpSharedAuctionActionBid';
import { PdpSharedAuctionUnavailable } from '../PdpSharedAuctionUnavailable/PdpSharedAuctionUnavailable';
import { PdpSharedAuctionSubscription } from '../PdpSharedAuctionSubscription/PdpSharedAuctionSubscription';
import { PdpSharedAuctionActionBinAndBid } from '../PdpSharedAuctionActions/PdpSharedAuctionActionBinAndBid';
import { PdpSharedAuctionActionCompletePurchase } from '../PdpSharedAuctionActions/PdpSharedAuctionActionCompletePurchase';
import { PdpSharedAuctionSecondChanceOffer } from '../PdpSharedAuctionActions/PdpSharedAuctionSecondChanceOffer';

import AuctionBiddingModal from 'dibs-auctions-bidding-modal/exports/AuctionBiddingModal';
import { BID_RESPONSE_ERRORS } from 'dibs-auctions-bidding-modal/exports/constants';

// types
import { PdpSharedAuctionInfo_item$key as Item } from './__generated__/PdpSharedAuctionInfo_item.graphql';
import { PdpSharedAuctionInfo_viewer$key } from './__generated__/PdpSharedAuctionInfo_viewer.graphql';
import { OnBidPlaced } from 'dibs-auctions-bidding-modal/exports/types';

// styles
import styles from './PdpSharedAuctionInfo.scss';

type Props = {
    viewer: PdpSharedAuctionInfo_viewer$key;
    item: Item;
    currency: string;
    promoCode: ReactNode;
    handleRoundPriceTracking?: () => void;
};

type RelayVariables = {
    userId: string;
    isClient: boolean;
};

type ReduxStoreType = {
    relayVariables: {
        variables: RelayVariables;
    };
};

export type OnAuctionBidClick = (hasBid?: boolean) => void;

const viewerFragment = graphql`
    fragment PdpSharedAuctionInfo_viewer on Viewer {
        ...PdpSharedAuctionActionBinAndBid_viewer
        ...PdpSharedAuctionActionCompletePurchase_viewer
        ...PdpSharedAuctionSecondChanceOffer_viewer
    }
`;

const itemFragment = graphql`
    fragment PdpSharedAuctionInfo_item on Item {
        isSold
        serviceId
        isUnavailable
        relevantAuctionLot(page: pdp) {
            serviceId
            endDate
        }
        relevantAuctionSolution(page: pdp) {
            reserveMet
            highestBidderId
        }
        lastTransaction(buyerId: $userId) @include(if: $fetchUser) {
            isCanceled
            checkoutDate
            isWinningAuctionOrder
            isSecondChanceOfferActive
            mostRecentOffer {
                id
                serviceId
                isExpired
            }
        }
        ...useAuctionSecondChanceInfo_item @arguments(page: pdp)
        ...PdpSharedAuctionSubscription_item
        ...PriceDisplay_item
            @arguments(
                userId: $userId
                isTrade: $isTrade
                userCountryCode: $userCountryCode
                priceBookName: $priceBookName
            )
        ...useAuctionState_item @arguments(page: pdp)
        ...useAuctionPricing_item @arguments(page: pdp)
        ...PdpSharedAuctionActionBinAndBid_item
        ...PdpSharedAuctionActionBid_item
        ...PdpSharedAuctionClosed_item
        ...PdpSharedAuctionActionCompletePurchase_item
        ...PdpSharedAuctionPreview_item
        ...PdpSharedAuctionSecondChanceOffer_item
        ...bidModalTrackingHelpers_item
        ...useOriginalListPrice_item @arguments(page: pdp)
    }
`;

export const PdpSharedAuctionInfo: FC<Props> = ({
    currency,
    item: itemRef,
    viewer: viewerRef,
    promoCode,
    handleRoundPriceTracking,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const {
        isPreviewAuction,
        isActiveAuction,
        isPausedAuction,
        isTransitionAuction,
        isUnavailableAuction,
        hasAuctionEndedWithBIN,
        hasAuctionEndedWithWinner,
        hasAuctionEndedWithNoWinner,
    } = useAuctionState({ item });
    const { isSecondChanceOfferSent } = useAuctionSecondChanceInfo({ item });
    const { hasOriginalListPrice } = useOriginalListPrice({ item, currency });
    const { isBuyItNowEligible, totalBids } = useAuctionPricing({
        item,
        currency,
    });
    const { userId, isClient } = useSelector<ReduxStoreType, RelayVariables>(
        relay => relay?.relayVariables?.variables || {}
    );
    const { isMobile } = useServerVarsContext();
    const { isSold, lastTransaction, relevantAuctionLot, relevantAuctionSolution, isUnavailable } =
        item || {};
    const {
        isBiddingDisabled,
        setIsBiddingDisabled,
        setShouldDisplayCountdown,
        dispatchUpdateAuctionItem,
    } = useSharedAuctionContext();

    const {
        mostRecentOffer,
        isWinningAuctionOrder,
        isSecondChanceOfferActive,
        checkoutDate: transactionCheckoutDate,
        isCanceled: isTransactionCanceled,
    } = lastTransaction || {};
    const { endDate, serviceId: auctionLotId } = relevantAuctionLot || {};
    const { reserveMet, highestBidderId } = relevantAuctionSolution || {};
    const itemIsUnavailable =
        isUnavailableAuction ||
        (hasAuctionEndedWithBIN && !isSold) || // item was sold with BIN and then canceled
        isUnavailable;
    const { isModalOpen, openModal, closeModal } = useAuctionBidModalState(
        !isBiddingDisabled && !!isActiveAuction
    );

    const onAuctionBidClick: OnAuctionBidClick = (hasBid = false) => {
        trackAuctionBidClick({ item, hasBid, hasOriginalListPrice });
        openModal();
    };

    const [isCloseToReserve, setIsCloseToReserve] = useState(false);

    const onBidPlaced: OnBidPlaced = bidError => {
        if (bidError.errorType === BID_RESPONSE_ERRORS.CLOSE_TO_RESERVE) {
            setIsCloseToReserve(true);
        } else if (isCloseToReserve) {
            setIsCloseToReserve(false);
        }
    };

    useEffect(() => {
        if (!isTransitionAuction) {
            setIsBiddingDisabled(false);
        } else {
            setIsBiddingDisabled(true);
        }
    }, [endDate, isTransitionAuction, setIsBiddingDisabled]);

    //We alwasys want to display the countdown if this component mounts
    //We only hide it if the `content` variable is still null after all the checks
    useEffect(() => {
        setShouldDisplayCountdown(true);
    }, [setShouldDisplayCountdown]);

    let content = null;
    let hideBorder = false;

    if (isSold) {
        // Move sold to first check.  If item is sold always show sold experience
        content = (
            <PdpSharedAuctionClosed
                item={item}
                currency={currency}
                linkToBidHistory={!!reserveMet && !isSecondChanceOfferSent}
                handleRoundPriceTracking={handleRoundPriceTracking}
            />
        );
    } else if (itemIsUnavailable) {
        content = <PdpSharedAuctionUnavailable />;
    } else if (isSecondChanceOfferActive && !transactionCheckoutDate) {
        content = (
            <PdpSharedAuctionSecondChanceOffer
                viewer={viewer}
                item={item}
                currency={currency}
                handleRoundPriceTracking={handleRoundPriceTracking}
            />
        );
    } else if (isPreviewAuction) {
        content = (
            <PdpSharedAuctionPreview
                item={item}
                currency={currency}
                promoCode={promoCode}
                handleRoundPriceTracking={handleRoundPriceTracking}
            />
        );
    } else if ((isActiveAuction || isTransitionAuction) && isBuyItNowEligible) {
        content = (
            <PdpSharedAuctionActionBinAndBid
                viewer={viewer}
                item={item}
                currency={currency}
                onAuctionBidClick={onAuctionBidClick}
                isBiddingDisabled={isBiddingDisabled}
                handleRoundPriceTracking={handleRoundPriceTracking}
            />
        );
    } else if (isActiveAuction || isTransitionAuction) {
        content = (
            <PdpSharedAuctionActionBid
                item={item}
                currency={currency}
                onAuctionBidClick={onAuctionBidClick}
                isBiddingDisabled={isBiddingDisabled}
                isCloseToReserve={isCloseToReserve}
                handleRoundPriceTracking={handleRoundPriceTracking}
            />
        );
    } else if (isPausedAuction || isSecondChanceOfferSent) {
        content = (
            <PdpSharedAuctionClosed
                item={item}
                currency={currency}
                linkToBidHistory={!!reserveMet && !isSecondChanceOfferSent}
                handleRoundPriceTracking={handleRoundPriceTracking}
            />
        );
        hideBorder = true;
    } else if (hasAuctionEndedWithWinner && isClient) {
        if (
            highestBidderId === userId &&
            (!lastTransaction ||
                (isWinningAuctionOrder &&
                    !transactionCheckoutDate &&
                    !mostRecentOffer?.isExpired &&
                    !isTransactionCanceled))
        ) {
            content = (
                <PdpSharedAuctionActionCompletePurchase
                    viewer={viewer}
                    item={item}
                    currency={currency}
                />
            );
        } else {
            content = <PdpSharedAuctionClosed item={item} currency={currency} linkToBidHistory />;
            hideBorder = true;
        }
    } else if (hasAuctionEndedWithNoWinner) {
        content = <PdpSharedAuctionClosed item={item} currency={currency} />;
        hideBorder = true;
    }

    //handles case when item gets relisted to marketplace and none of the above conditions are met
    if (!content) {
        setShouldDisplayCountdown(false);
    }

    return (
        <>
            {content && (
                <div
                    className={classnames(styles.container, {
                        [styles.divider]:
                            !isSold && !itemIsUnavailable && !hideBorder && !totalBids,
                    })}
                >
                    {content}
                </div>
            )}
            {!isPreviewAuction && promoCode}
            <PdpSharedAuctionSubscription refetch={dispatchUpdateAuctionItem} item={item} />
            {isModalOpen && (
                <AuctionBiddingModal
                    currency={currency}
                    itemId={item?.serviceId || ''}
                    onClose={closeModal}
                    isMobile={!!isMobile}
                    isBiddingDisabled={isBiddingDisabled}
                    onBidPlaced={onBidPlaced}
                    auctionLotId={auctionLotId || ''}
                />
            )}
        </>
    );
};
